// import './index.css';

// Style
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dropzone/styles.css';
import '@mantine/carousel/styles.css';
import '@mantine/dates/styles.css';
import 'mantine-react-table/styles.css';
import 'mantine-contextmenu/styles.layer.css';
import '@app/styles/global.css';

// Providers
import { PropsWithChildren, Suspense } from 'react';
import { LoadingOverlay, MantineProvider } from '@mantine/core';
import { ModalsProvider as MantinePromptProvider } from '@mantine/modals';
import { DatesProvider } from '@mantine/dates';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import 'dayjs/locale/fr';
import 'dayjs/locale/en';

dayjs.extend(localizedFormat);
import { useAuth0 } from '@auth0/auth0-react';
import ApolloWrapper from '@services/apollo/ApolloWrapper';
import TranslationsProvider from '@services/translation/TranslationsProvider';
import UserActivityProvider from '@features/User/UserActivityProvider';
import ViewAsProvider from '@shared/providers/ViewAsProvider';

// UI lib components
import { Notifications } from '@mantine/notifications';

// UI local components
import ClientDndContext from '@ui-kit/organisms/ClientDndContext';
import MobileGuard from '@ui-kit/organisms/MobileGuard';
import WorkspaceRouter from '@routers/WorkspaceRouter';

// Style
import { light } from '@shared/themes';
import * as notificationsClasses from '@app/styles/notifications.css';

function LocalizedDatesProvider({ children }: PropsWithChildren) {
  const { i18n } = useTranslation();
  return (
    <DatesProvider settings={{ locale: i18n.language }}>
      {children}
    </DatesProvider>
  );
}

function App() {
  const { isLoading } = useAuth0();

  if (isLoading) {
    return null;
  }

  return (
    <ApolloWrapper>
      <ViewAsProvider>
        <UserActivityProvider>
          <TranslationsProvider defaultNS={['Common', 'GateLayout']}>
            <MantineProvider
              theme={{
                primaryColor: 'blackroomBlue',
                ...light,
              }}
            >
              <LocalizedDatesProvider>
                <Notifications
                  position="top-right"
                  classNames={{ root: notificationsClasses.root }}
                  styles={{
                    // Mantine v7's notifications root is a near full-width
                    // fixed container; without this, the empty container
                    // intercepts clicks on whatever is rendered below it.
                    root: { pointerEvents: 'none' },
                    notification: { pointerEvents: 'auto' },
                  }}
                />
                <MantinePromptProvider>
                  <Suspense
                    fallback={
                      <LoadingOverlay
                        overlayProps={{ blur: 3 }}
                        bg="white"
                        visible
                      />
                    }
                  >
                    <ClientDndContext>
                      <MobileGuard>
                        <WorkspaceRouter />
                      </MobileGuard>
                    </ClientDndContext>
                  </Suspense>
                </MantinePromptProvider>
              </LocalizedDatesProvider>
            </MantineProvider>
          </TranslationsProvider>
        </UserActivityProvider>
      </ViewAsProvider>
    </ApolloWrapper>
  );
}

export default App;
